import { getLogger } from '@confluence/logger';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';

import { PRELOAD_TIMEOUT } from './mouseTraceTypes.ts';
import type { MouseTraceEntry, MouseTraceModel } from './mouseTraceTypes.ts';

const logger = getLogger('predictLeaveProbability');

// NOTE: while model should (and will) be fetched from Statsig, we need to have a default model
// to avoid errors when the model is not available or buggy. The default model is based on the
// data collected from mouse tracing on Hello over about 4 hours.
const defaultModel: MouseTraceModel = {
	version: 'default',
	distanceWeight: 0.106,
	speedWeight: 0.072,
	xRangeWeight: 0.095,
	yRangeWeight: 0.048,
	threshold: 0.5,
	stats: {
		distance: { mean: 21.22, stdDev: 30.59 },
		speed: { mean: 0.14, stdDev: 0.21 },
		xRange: { mean: 15.13, stdDev: 30.61 },
		yRange: { mean: 8.52, stdDev: 5.62 },
	},
};

const calculateDistance = (p1: MouseTraceEntry, p2: MouseTraceEntry) => {
	return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
};

function normalizeFeatures(
	features: Record<string, number>,
	stats: Record<string, { mean: number; stdDev: number }>,
) {
	return {
		distance: (features.distance - stats.distance.mean) / stats.distance.stdDev,
		speed: (features.speed - stats.speed.mean) / stats.speed.stdDev,
		xRange: (features.xRange - stats.xRange.mean) / stats.xRange.stdDev,
		yRange: (features.yRange - stats.yRange.mean) / stats.yRange.stdDev,
	};
}

const extractEarlyTraceFeatures = (trace: MouseTraceEntry[], threshold = PRELOAD_TIMEOUT) => {
	const earlyPoints = trace.filter((p) => p.t <= threshold);

	if (earlyPoints.length < 2) {
		return null;
	}

	let totalDistance = 0;
	for (let i = 1; i < earlyPoints.length; i++) {
		totalDistance += calculateDistance(earlyPoints[i - 1], earlyPoints[i]);
	}

	const timeSpan = earlyPoints[earlyPoints.length - 1].t - earlyPoints[0].t;
	const avgSpeed = timeSpan !== 0 ? totalDistance / timeSpan : 0;

	const xValues = earlyPoints.map((p) => p.x);
	const yValues = earlyPoints.map((p) => p.y);
	const xRange = Math.max(...xValues) - Math.min(...xValues);
	const yRange = Math.max(...yValues) - Math.min(...yValues);

	const result = {
		distance: totalDistance,
		speed: avgSpeed,
		xRange,
		yRange,
	};

	return result;
};

export const predictLeaveProbability = (trace: MouseTraceEntry[]) => {
	const modelStr = StatsigConfigurations.getString('confluence-delayed-preload-model', '');
	let model = defaultModel;
	try {
		model = JSON.parse(modelStr) as MouseTraceModel;
	} catch (error) {
		logger.debug`Error parsing model - ${error}`;
	}

	const result = {
		version: model.version,
		leaveProbability: 0.5,
	};

	try {
		const features = extractEarlyTraceFeatures(trace);
		if (!features) {
			return result;
		}

		const normalizedFeatures = normalizeFeatures(features, model.stats);

		const score =
			normalizedFeatures.distance * model.distanceWeight +
			normalizedFeatures.speed * model.speedWeight +
			normalizedFeatures.xRange * model.xRangeWeight +
			normalizedFeatures.yRange * model.yRangeWeight;

		// Convert to probability using sigmoid function
		result.leaveProbability = 1 / (1 + Math.exp(-score));
	} catch (error) {
		logger.debug`Error predicting leave probability - ${error}`;
	}

	return result;
};
